// Financial Reporting suite — /portfolio-admin/reporting. Portfolio-scope
// reporting surface: stat tiles → due strip → portfolio-series cards
// (FR-Q2 / FR-A1; the BMSP pair appears once trigger-armed activation
// fires) → instance history (inside the cards) → the ADMIN series catalog
// (every series row with activation-state flips — the phase-2 switch).
// Dormant series are ABSENT everywhere above the catalog, by design.

import { Button, Card, PageHeader, Skeleton } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  usePortfolioReportingOverview,
  useReportSeriesCatalog,
} from "@/hooks/portfolioAdmin/useFinancialReporting";
import { ReportingProduceSection } from "@/components/portfolioAdmin/reporting/ReportingProduceSection";
import { SeriesCatalogTable } from "@/components/portfolioAdmin/reporting/SeriesCatalogTable";

export default function ReportingPage() {
  const auth = useAuth();
  const canManageCatalog =
    auth.hasPermission("FinancialReporting.ManageSeriesCatalog") ||
    auth.hasRoleCode("ADMIN");

  const overview = usePortfolioReportingOverview();
  const catalog = useReportSeriesCatalog(canManageCatalog);

  return (
    <div className="flex flex-col gap-space-4" data-testid="reporting-page">
      <PageHeader
        title="Reporting"
        subtitle="Financial Reporting suite — portfolio-level series, due dates and filings."
      />

      {overview.isLoading && (
        <div className="flex flex-col gap-space-2" data-testid="reporting-page-loading">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {overview.isError && (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            Could not load the reporting overview.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-space-2"
            onClick={() => void overview.refetch()}
          >
            Retry
          </Button>
        </Card>
      )}

      {overview.data && (
        <ReportingProduceSection overview={overview.data} loanId={null} />
      )}

      {canManageCatalog && catalog.data && (
        <SeriesCatalogTable rows={catalog.data} />
      )}
    </div>
  );
}
